import * as React from "react";
import { Calendar as CalendarIcon, X as XIcon } from "lucide-react";
import { format, parse, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";

import { Input } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Calendar } from "./calendar";
import { Label } from "./label";
import { buttonVariants } from "./button";
import { cn } from "@/lib/utils";

interface DateInputProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  formatString?: string; // default: 'dd/MM/yyyy'
  inputClassName?: string;
}

// Só no formato brasileiro: digita-se 15082026 e aparece 15/08/2026.
function aplicarMascaraDiaMesAno(texto: string) {
  const d = texto.replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

export function DateInput({
  value,
  onChange,
  label,
  placeholder = "Selecione ou digite a data",
  required = false,
  disabled = false,
  id = "date-input",
  formatString = "dd/MM/yyyy",
  inputClassName,
}: DateInputProps) {
  const [inputValue, setInputValue] = React.useState<string>(
    value ? format(value, formatString) : "",
  );
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (value) {
      setInputValue(format(value, formatString));
    } else {
      setInputValue("");
    }
  }, [value, formatString]);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val =
      formatString === "dd/MM/yyyy" ? aplicarMascaraDiaMesAno(e.target.value) : e.target.value;
    setInputValue(val);
    const parsed = parse(val, formatString, new Date(), { locale: ptBR });
    if (isValid(parsed)) {
      onChange(parsed);
    } else if (val === "") {
      onChange(null);
    }
  }

  function handleCalendarSelect(date?: Date) {
    if (date) {
      setInputValue(format(date, formatString));
      onChange(date);
      setOpen(false);
    }
  }

  function handleClear() {
    setInputValue("");
    onChange(null);
    inputRef.current?.focus();
  }

  return (
    <div className="flex w-full flex-col justify-start gap-2">
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <span className="text-red-500"> *</span>}
        </Label>
      )}
      <div className="relative">
        <Input
          id={id}
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className={cn("w-full rounded-md border-2 border-gray-400 pr-20", inputClassName)}
        />
        {/* Botões à direita do input */}
        <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-2">
          {inputValue && (
            <button
              type="button"
              aria-label="Limpar data"
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
              onClick={handleClear}
              tabIndex={-1}
              disabled={disabled}
            >
              <XIcon className="h-4 w-4" />
            </button>
          )}
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger
              type="button"
              className={buttonVariants({
                variant: "ghost",
                size: "icon",
                className: "h-8 w-8 p-0",
              })}
              disabled={disabled}
              aria-label="Abrir calendário"
            >
              <CalendarIcon className="h-4 w-4" />
            </PopoverTrigger>
            <PopoverContent align="end" className="pointer-events-auto w-auto bg-white p-0">
              <Calendar
                mode="single"
                selected={value ?? undefined}
                onSelect={handleCalendarSelect}
                initialFocus
                locale={ptBR}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
}
